/**
 * MobileHomeView — TODO-MDC-23.
 *
 * Vista simplificada del Dashboard para mobile (<md). Filosofía
 * acordada con el usuario:
 *
 *   "La versión móvil podría ser muy simplificada y limitarse a algunas
 *    acciones específicas (las más prácticas) y, por otro lado, recibir
 *    las notificaciones de todo para ir al escritorio a operar"
 *
 * Lo que SÍ va en mobile:
 *   - 3 KPIs grandes legibles (total adeudado, comprobantes pendientes,
 *     facturado del mes).
 *   - Centro de avisos accionables (Insights ya existentes pero como
 *     stack vertical full-width).
 *   - Acciones rápidas: 4 botones que abren los flows que SÍ vale la pena
 *     hacer en mobile (Recupero, Facturación, Conciliación, Mi perfil).
 *   - Banner explícito al final: "Para tareas avanzadas (configuración,
 *     plantillas, importes masivos) abrí MANAXER en computadora".
 *
 * Lo que NO va en mobile:
 *   - Charts (cash-flow, série mensual, top admins, top morosos): muy
 *     densas, no se leen bien en pantallas chicas.
 *   - Aging detallado: solo se muestra el % consolidado.
 *   - Riesgo y carteras secundarias: el operador puede entrar al módulo
 *     directo si necesita.
 *
 * Realtime: hereda los listeners del DashboardPage padre (la data se
 * recarga ahí).
 */

import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  HandCoins,
  Landmark,
  Receipt,
  TrendingUp,
  User,
} from 'lucide-react';
import { routes } from '@/lib/routes';
import { cn } from '@/lib/cn';
import type { Empresa } from '@/services/api/empresas';
import type { DashboardKpis, MorosidadKpis } from '@/services/api/reportes';
import {
  InsightsCarousel,
  type InsightsSlide,
} from '@/components/common/InsightsCarousel';
import { EmailQueueHealthBanner } from '@/components/common/EmailQueueHealthBanner';
import { ExtractoIncompletoBanner } from '@/modules/finanzas/components/ExtractoIncompletoBanner';

interface Props {
  empresa: Empresa;
  fullName: string | null | undefined;
  kpis: DashboardKpis | null;
  morosidad: MorosidadKpis | null;
  insightsSlides: InsightsSlide[];
}

export function MobileHomeView({
  empresa,
  fullName,
  kpis,
  morosidad,
  insightsSlides,
}: Props) {
  const totalAdeudado = morosidad?.total_adeudado ?? 0;
  const facturadoMes = kpis?.facturado_mes ?? 0;
  const comprobantesMes = kpis?.comprobantes_mes ?? 0;
  const borradoresMes = kpis?.borradores_mes ?? 0;
  const movPendientes = kpis?.movimientos_pendientes ?? 0;

  return (
    <div className="space-y-4 pb-6">
      {/* ── Header compacto ───────────────────────────────────────────── */}
      <header>
        <p className="kicker mb-1">Panel principal</p>
        <h1 className="text-2xl font-black tracking-tight text-neutral-ink">
          Hola, <span className="text-blue">{firstName(fullName)}</span>
        </h1>
        <p className="mt-0.5 text-xs text-neutral-muted">{empresa.nombre}</p>
      </header>

      {/* ── Banners (extractos colgados, cola de email caída) ─────────── */}
      <ExtractoIncompletoBanner empresaId={empresa.id} />
      <EmailQueueHealthBanner empresaId={empresa.id} />

      {/* ── KPIs grandes ────────────────────────────────────────────── */}
      <BigKpiCard
        kicker="Total adeudado"
        value={fmtMoney(totalAdeudado)}
        sub={`${morosidad?.comprobantes_con_deuda ?? 0} comprobante${(morosidad?.comprobantes_con_deuda ?? 0) === 1 ? '' : 's'} con deuda · ${
          morosidad ? Math.round(morosidad.morosidad_pct) : 0
        }% morosidad`}
        tone="orange"
        to={routes.empresa.recupero(empresa.id)}
      />

      <div className="grid grid-cols-2 gap-3">
        <SmallKpiCard
          kicker="Pendientes"
          value={String(borradoresMes)}
          sub={`de ${comprobantesMes} este mes`}
          tone="blue"
          to={routes.empresa.facturacion(empresa.id)}
        />
        <SmallKpiCard
          kicker="Facturado mes"
          value={fmtMoneyShort(facturadoMes)}
          sub="autorizado en ARCA"
          tone="success"
          to={routes.empresa.facturacion(empresa.id)}
        />
      </div>

      {movPendientes > 0 && (
        <Link
          to={routes.empresa.finanzas(empresa.id)}
          className="card-premium bg-orange-10/40 flex items-center gap-3 border-l-4 border-orange p-3"
        >
          <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-orange/15 text-orange">
            <AlertTriangle size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-neutral-ink">
              {movPendientes} movimiento{movPendientes === 1 ? '' : 's'} sin identificar
            </p>
            <p className="text-[11px] text-neutral-muted">
              Necesitan asignación a edificio o admin
            </p>
          </div>
          <ArrowRight size={14} className="text-orange" />
        </Link>
      )}

      {/* ── Insights / avisos accionables ─────────────────────────── */}
      {insightsSlides.length > 0 && (
        <div>
          <p className="kicker mb-2">Avisos para vos</p>
          <InsightsCarousel slides={insightsSlides} />
        </div>
      )}

      {/* ── Acciones rápidas ───────────────────────────────────────── */}
      <div>
        <p className="kicker mb-2">Acciones rápidas</p>
        <div className="grid grid-cols-2 gap-3">
          <QuickAction
            to={routes.empresa.recupero(empresa.id)}
            icon={<HandCoins size={20} />}
            label="Recupero"
            sub="Mandá recordatorios"
            tone="orange"
          />
          <QuickAction
            to={routes.empresa.facturacion(empresa.id)}
            icon={<Receipt size={20} />}
            label="Facturación"
            sub="Ver lotes y borradores"
            tone="blue"
          />
          <QuickAction
            to={routes.empresa.finanzas(empresa.id)}
            icon={<Landmark size={20} />}
            label="Finanzas"
            sub="Movimientos y cajas"
            tone="success"
          />
          <QuickAction
            to={routes.empresa.perfil(empresa.id)}
            icon={<User size={20} />}
            label="Mi perfil"
            sub="Datos y sesión"
            tone="muted"
          />
        </div>
      </div>

      {/* ── Banner: para más, en computadora ────────────────────── */}
      <div className="card-premium border border-dashed border-blue-30 bg-blue-10/30 p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-blue text-white">
            <ExternalLink size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-neutral-ink">
              ¿Hay tareas más complejas?
            </p>
            <p className="mt-1 text-[11px] text-neutral-muted">
              Para configuración avanzada, conciliación bancaria masiva, plantillas de
              email, importes masivos y manejo de usuarios, te recomendamos abrir{' '}
              <b>MANAXER</b> en una computadora — la pantalla grande hace la diferencia.
            </p>
            <p className="mt-2 text-[11px] text-neutral-muted">
              <TrendingUp size={11} className="mr-1 inline" />
              Las notificaciones llegan al celu igual, así sabés cuándo pasar al
              escritorio.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────

function BigKpiCard({
  kicker,
  value,
  sub,
  tone,
  to,
}: {
  kicker: string;
  value: string;
  sub: string;
  tone: 'orange' | 'blue' | 'success';
  to: string;
}) {
  const cls = {
    orange: 'border-orange-30 bg-orange-10/40',
    blue: 'border-blue-30 bg-blue-10/40',
    success: 'border-status-success/30 bg-status-success/5',
  }[tone];
  return (
    <Link
      to={to}
      className={cn('card-premium block p-4 transition active:scale-[0.99]', cls)}
    >
      <p className="kicker">{kicker}</p>
      <p className="mt-1 text-3xl font-black tracking-tight text-neutral-ink">{value}</p>
      <p className="mt-1 text-[11px] text-neutral-muted">{sub}</p>
    </Link>
  );
}

function SmallKpiCard({
  kicker,
  value,
  sub,
  tone,
  to,
}: {
  kicker: string;
  value: string;
  sub: string;
  tone: 'orange' | 'blue' | 'success';
  to: string;
}) {
  const cls = {
    orange: 'border-orange-30 bg-orange-10/40',
    blue: 'border-blue-30 bg-blue-10/40',
    success: 'border-status-success/30 bg-status-success/5',
  }[tone];
  return (
    <Link
      to={to}
      className={cn('card-premium block p-3 transition active:scale-[0.99]', cls)}
    >
      <p className="kicker text-[10px]">{kicker}</p>
      <p className="mt-1 text-xl font-black tracking-tight text-neutral-ink">{value}</p>
      <p className="mt-0.5 text-[10px] text-neutral-muted">{sub}</p>
    </Link>
  );
}

function QuickAction({
  to,
  icon,
  label,
  sub,
  tone,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  sub: string;
  tone: 'orange' | 'blue' | 'success' | 'muted';
}) {
  const cls = {
    orange: 'bg-orange/10 text-orange border-orange/20',
    blue: 'bg-blue/10 text-blue border-blue/20',
    success: 'bg-status-success/10 text-status-success border-status-success/20',
    muted: 'bg-neutral-soft text-neutral-ink border-surface-line',
  }[tone];
  return (
    <Link
      to={to}
      className="card-premium flex flex-col gap-1 p-3 transition active:scale-[0.99]"
    >
      <span
        className={cn('mb-1 grid h-10 w-10 place-items-center rounded-md border', cls)}
      >
        {icon}
      </span>
      <p className="text-sm font-bold text-neutral-ink">{label}</p>
      <p className="text-[11px] text-neutral-muted">{sub}</p>
    </Link>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function firstName(fullName: string | null | undefined): string {
  if (!fullName) return 'MANAXER';
  return fullName.trim().split(/\s+/)[0] ?? 'MANAXER';
}

function fmtMoney(n: number): string {
  return n.toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  });
}

function fmtMoneyShort(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return fmtMoney(n);
}
